import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Input,
  Label,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  toast
} from "@veritrellis/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Navigate } from "react-router-dom";
import { PageHeader } from "../components/PageHeader.js";
import { FormField } from "../components/FormField.js";
import { apiFetch } from "../lib/api.js";
import { usePersistedAdminEnvironment } from "../hooks/usePersistedAdminEnvironment.js";
import { useWorkspaceId } from "../hooks/useWorkspaceId.js";

type ApiKeyEnvironment = "sandbox" | "production";

type ApiKeyRow = {
  id: string;
  name: string;
  environment: string;
  prefix: string;
  created_at: string;
  revoked_at: string | null;
};

type AccessManifestResponse = {
  manifest: {
    can: {
      mutate_api_keys_sandbox: boolean;
      mutate_api_keys_production: boolean;
    };
  };
};

const ApiKeyCreate = (input: {
  workspaceId: string;
  environment: ApiKeyEnvironment;
  canCreate: boolean;
}) => {
  const queryClient = useQueryClient();
  const [name, setName] = useState("dev");
  const [token, setToken] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: async () => {
      const res = await apiFetch(`/admin/workspaces/${input.workspaceId}/api-keys`, {
        method: "POST",
        body: JSON.stringify({ name, environment: input.environment })
      });
      const json = (await res.json()) as { token?: string; id?: string; prefix?: string };
      if (!res.ok) {
        throw new Error(`create_${String(res.status)}`);
      }
      return json;
    },
    onSuccess: (json) => {
      setToken(json.token ?? null);
      void queryClient.invalidateQueries({ queryKey: ["api-keys", input.workspaceId] });
      toast.show({
        title: "Created",
        message: "API key created. Copy the token now; it will not be shown again.",
        color: "green"
      });
    },
    onError: (err: unknown) => {
      const code = err instanceof Error ? err.message : "";
      const msg =
        code === "create_403"
          ? "Your role cannot create API keys in this environment (production keys require owner or admin)."
          : "Could not create API key.";
      toast.show({ title: "Error", message: msg, color: "red" });
    }
  });

  return (
    <section className="admin-panel" aria-labelledby="api-keys-create-heading">
      <div className="admin-panel-header">
        <h2 id="api-keys-create-heading" className="admin-panel-title">
          Create key
        </h2>
      </div>
      <div className="admin-panel-body-padded">
        <p className="admin-settings-row-desc" style={{ margin: "0 0 1rem" }}>
          Secrets are only shown once. Tokens are prefixed with{" "}
          <code className="admin-mono-inline">tr_{input.environment}_</code>…
        </p>
        {!input.canCreate ? (
          <Alert className="border-amber-200 bg-amber-50" style={{ marginBottom: "1rem" }}>
            <AlertTitle>Cannot create in this environment</AlertTitle>
            <AlertDescription>
              Sandbox keys: owner, admin, or developer. Production keys: owner or admin only.
            </AlertDescription>
          </Alert>
        ) : null}
        <FormField>
          <Label>Name</Label>
          <Input value={name} onChange={(e) => setName(e.currentTarget.value)} />
        </FormField>
        <div style={{ marginTop: "1rem" }}>
          <Button disabled={!input.canCreate || create.isPending} onClick={() => create.mutate()}>
            {create.isPending ? "Creating…" : "Create"}
          </Button>
        </div>
        {token ? (
          <pre className="admin-api-key-secret" style={{ marginTop: "1rem" }}>
            {token}
          </pre>
        ) : null}
      </div>
    </section>
  );
};

export const SettingsApiKeysPage = () => {
  const queryClient = useQueryClient();
  const { workspaceId, isLoading } = useWorkspaceId();
  const [keyEnvironment] = usePersistedAdminEnvironment();

  const manifest = useQuery({
    queryKey: ["access-manifest", workspaceId],
    enabled: workspaceId !== null,
    queryFn: async () => {
      const res = await apiFetch(`/admin/workspaces/${workspaceId}/access/manifest`);
      if (!res.ok) {
        throw new Error(`manifest_${String(res.status)}`);
      }
      return (await res.json()) as AccessManifestResponse;
    }
  });

  const canMutateInSelectedEnv =
    keyEnvironment === "sandbox"
      ? (manifest.data?.manifest.can.mutate_api_keys_sandbox ?? false)
      : (manifest.data?.manifest.can.mutate_api_keys_production ?? false);

  const q = useQuery({
    queryKey: ["api-keys", workspaceId, keyEnvironment],
    enabled: workspaceId !== null,
    queryFn: async () => {
      const res = await apiFetch(
        `/admin/workspaces/${workspaceId}/api-keys?environment=${keyEnvironment}`
      );
      if (!res.ok) {
        throw new Error("api_keys_failed");
      }
      return (await res.json()) as { api_keys: ApiKeyRow[] };
    }
  });

  const revoke = useMutation({
    mutationFn: async (keyId: string) => {
      if (workspaceId === null) {
        throw new Error("no_workspace");
      }
      const res = await apiFetch(`/admin/workspaces/${workspaceId}/api-keys/${keyId}`, {
        method: "DELETE"
      });
      if (!res.ok) {
        throw new Error(`revoke_${String(res.status)}`);
      }
    },
    onSuccess: async () => {
      if (workspaceId !== null) {
        await queryClient.invalidateQueries({ queryKey: ["api-keys", workspaceId] });
      }
      toast.show({
        title: "Revoked",
        message: "This API key can no longer be used.",
        color: "green"
      });
    },
    onError: (err: unknown) => {
      const code = err instanceof Error ? err.message : "";
      const msg =
        code === "revoke_403"
          ? "Your role cannot revoke keys in this environment."
          : "Could not revoke API key.";
      toast.show({ title: "Error", message: msg, color: "red" });
    }
  });

  if (isLoading) {
    return <p className="text-sm">Loading…</p>;
  }
  if (workspaceId === null) {
    return <Navigate to="/onboarding" replace />;
  }
  if (manifest.isLoading || q.isLoading) {
    return <p className="text-sm">Loading API keys…</p>;
  }
  if (manifest.isError || q.isError) {
    return <p className="text-sm text-destructive">Failed to load API keys or permissions.</p>;
  }
  const keys = q.data?.api_keys ?? [];

  return (
    <div className="admin-page-stack">
      <PageHeader
        title="API keys"
        description="Authenticate authorization calls per environment. Use the Sandbox / Production switch in the header. Rotate by revoking and creating a new key."
      />

      <ApiKeyCreate
        workspaceId={workspaceId}
        environment={keyEnvironment}
        canCreate={canMutateInSelectedEnv}
      />

      <section className="admin-panel" aria-labelledby="api-keys-list-heading">
        <div className="admin-panel-header">
          <h2 id="api-keys-list-heading" className="admin-panel-title">
            {keyEnvironment} keys ({keys.length})
          </h2>
        </div>
        <div className="admin-panel-body">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Environment</TableHead>
                <TableHead>Prefix</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-[120px]" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {keys.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6}>
                    <span className="text-sm text-muted-foreground">
                      No keys in {keyEnvironment}. Create one above.
                    </span>
                  </TableCell>
                </TableRow>
              ) : (
                keys.map((k) => (
                  <TableRow key={k.id}>
                    <TableCell>{k.name}</TableCell>
                    <TableCell>
                      <Badge variant={k.environment === "production" ? "warning" : "secondary"}>
                        {k.environment}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <code className="admin-mono-inline">{k.prefix}…</code>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm">{new Date(k.created_at).toLocaleString()}</span>
                    </TableCell>
                    <TableCell>
                      {k.revoked_at ? (
                        <span className="text-sm text-destructive">
                          Revoked {new Date(k.revoked_at).toLocaleString()}
                        </span>
                      ) : (
                        <span className="admin-cap-yes">Active</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-end gap-1">
                        {k.revoked_at === null ? (
                          <Button
                            size="sm"
                            variant="destructive"
                            disabled={!canMutateInSelectedEnv || revoke.isPending}
                            onClick={() => {
                              if (
                                globalThis.confirm(
                                  `Revoke API key “${k.name}” (${k.prefix}…)? This cannot be undone.`
                                )
                              ) {
                                revoke.mutate(k.id);
                              }
                            }}
                          >
                            Revoke
                          </Button>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </section>

      <p className="text-sm text-muted-foreground">
        Full secret values are never stored or shown after creation. Use separate keys per
        environment and rotate on a schedule.
      </p>
    </div>
  );
};
